import { useRef, useState } from "react";
import { useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Modal } from "@/components/design/Modal";
import { TextInput } from "@/components/design/TextField";
import {
  InviteExistingPersonDocument,
  PersonLoginAction,
  PersonLoginInvitationOutcome,
  ResendExistingPersonInvitationDocument,
  type PersonLoginAssignmentFieldsFragment,
} from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";

// Binds a login to a person who already exists in genesis. Deliberately NOT
// InviteOutsiderDialog with a person picker bolted on: that one takes an
// address and creates somebody new, this one takes a person and gives that
// exact profile a login (@external-adr GENESIS-0046-a-null-subject-means-no-account-binding).
//
// The recipient is always confirmed here and never sent straight from a table
// row. A stored address is only ever a suggestion: an import may carry any
// provisional address, and a syntactically real one is no more trustworthy than
// a reserved placeholder, which genesis reports as no address at all.
//
// The outcomes are business results, not errors: only a transport or permission
// failure routes through userErrorMessage(). Two of them are deliberately not
// offered a retry button: SEND_UNCERTAIN and CREATE_UNCERTAIN mean nobody
// knows what reached Keycloak, and a second press is what sends the second mail.

const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// The outcomes after which a second press is worth offering: nothing happened,
// or the realm refused the mail and the binding stands.
const RETRYABLE = new Set([
  PersonLoginInvitationOutcome.EmailConflict,
  PersonLoginInvitationOutcome.AccountChanged,
  PersonLoginInvitationOutcome.SendFailed,
]);

type InviteDialogMode = "invite" | "resend";

type Result =
  | { kind: "outcome"; outcome: PersonLoginInvitationOutcome }
  | { kind: "error"; message: string };

interface InviteExistingPersonDialogProps {
  // Always the row the administrator picked. The caller mounts this per person,
  // so there is no "which person is it now" state to reset and no effect to
  // write it with.
  assignment: PersonLoginAssignmentFieldsFragment;
  onClose: () => void;
  onCompleted: (assignment: PersonLoginAssignmentFieldsFragment) => void;
}

export function InviteExistingPersonDialog({
  assignment,
  onClose,
  onCompleted,
}: InviteExistingPersonDialogProps) {
  const intl = useIntl();
  const [email, setEmail] = useState(assignment.suggestedEmail ?? "");
  const [invalid, setInvalid] = useState(false);
  const [result, setResult] = useState<Result | null>(null);
  // Which command the next press runs; null once there is nothing left to press.
  const [mode, setMode] = useState<InviteDialogMode | null>("invite");
  const attempt = useRef<{ email: string; requestId: string } | null>(null);
  const submitting = useRef(false);
  const [invite, { loading: inviting }] = useMutation(InviteExistingPersonDocument);
  const [resend, { loading: resending }] = useMutation(ResendExistingPersonInvitationDocument);
  const loading = inviting || resending;

  const submit = async () => {
    if (loading || submitting.current || mode === null) return;
    const trimmed = email.trim();
    if (!EMAIL_SHAPE.test(trimmed)) {
      setInvalid(true);
      setResult(null);
      return;
    }
    setInvalid(false);
    setResult(null);

    // One key per user-confirmed attempt: a double click or a retried request
    // must not become a second account or a second mail.
    if (!attempt.current || attempt.current.email !== trimmed) {
      attempt.current = { email: trimmed, requestId: crypto.randomUUID() };
    }
    const requestId = attempt.current.requestId;
    submitting.current = true;
    const variables = { input: { personId: assignment.personId, email: trimmed, requestId } };
    try {
      const payload = mode === "resend"
        ? (await resend({ variables })).data?.resendExistingPersonInvitation
        : (await invite({ variables })).data?.inviteExistingPerson;
      if (!payload) throw new Error("the invitation returned no outcome");
      setResult({ kind: "outcome", outcome: payload.outcome });
      setMode(nextMode(payload.outcome, payload.assignment));
      attempt.current = null;
      onCompleted(payload.assignment);
    } catch (e) {
      setResult({ kind: "error", message: userErrorMessage(intl, e) });
    } finally {
      submitting.current = false;
    }
  };

  const submitLabel = mode === "resend"
    ? intl.formatMessage({ id: "loginAssignment.dialog.resendSubmit", defaultMessage: "Resend to this address" })
    : intl.formatMessage({ id: "loginAssignment.dialog.inviteSubmit", defaultMessage: "Invite to this address" });

  return (
    <Modal
      id="login-assignment-dialog"
      open
      onClose={onClose}
      dismiss="explicit"
      dirty={mode !== null && email !== (assignment.suggestedEmail ?? "")}
      preventClose={loading}
      title={intl.formatMessage({ id: "loginAssignment.dialog.eyebrow", defaultMessage: "Access · Login assignment" })}
      footer={
        <>
          <Button id="login-assignment-dialog-cancel" variant="secondary" onClick={onClose} disabled={loading}>
            {mode === null
              ? <FormattedMessage id="common.close" defaultMessage="Close" />
              : <FormattedMessage id="common.cancel" defaultMessage="Cancel" />}
          </Button>
          {mode !== null ? (
            <Button
              id="login-assignment-dialog-submit"
              variant="primary"
              onClick={() => void submit()}
              disabled={loading || email.trim() === ""}
            >
              {loading ? <FormattedMessage id="invite.sending" defaultMessage="Sending…" /> : submitLabel}
            </Button>
          ) : null}
        </>
      }
    >
      <h2 className="font-serif text-smycles-display-s leading-tight">
        <FormattedMessage id="loginAssignment.dialog.inviteHeading" defaultMessage="Give this person a login" />
      </h2>

      <p className="mt-2 text-smycles-meta leading-relaxed text-ink">
        <FormattedMessage
          id="loginAssignment.dialog.target"
          defaultMessage="The login is bound to {name}. Their profile, notes and contacts stay exactly where they are."
          values={{ name: <strong className="font-medium text-ink">{assignment.name}</strong> }}
        />
      </p>

      <label className="smycles-field-label mb-1 mt-6 block" htmlFor="login-assignment-email">
        <FormattedMessage id="loginAssignment.dialog.emailLabel" defaultMessage="Recipient" />
      </label>
      <TextInput
        id="login-assignment-email"
        type="email"
        value={email}
        autoComplete="off"
        disabled={mode === null}
        placeholder={intl.formatMessage({ id: "invite.emailPlaceholder", defaultMessage: "name@partner.example" })}
        onChange={(e) => {
          setEmail(e.target.value);
          setInvalid(false);
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter") void submit();
        }}
        aria-invalid={invalid || undefined}
        aria-describedby={invalid ? "login-assignment-email-error" : "login-assignment-email-hint"}
      />
      <p id="login-assignment-email-hint" className="mt-2 text-smycles-meta leading-relaxed text-ink-muted">
        {assignment.suggestedEmail
          ? <FormattedMessage
              id="loginAssignment.dialog.suggestionHint"
              defaultMessage="Prefilled from the profile. Check it: an imported address may be provisional."
            />
          : <FormattedMessage
              id="loginAssignment.dialog.noSuggestionHint"
              defaultMessage="No usable address is stored for this person. Enter the one the invitation should go to."
            />}
      </p>

      {invalid ? (
        <p id="login-assignment-email-error" className="mt-2 text-smycles-meta text-ink-accent" role="alert">
          <FormattedMessage id="invite.invalidEmail" defaultMessage="Please enter a valid email address." />
        </p>
      ) : null}

      {result ? (
        <p
          id="login-assignment-dialog-result"
          className="mt-4 text-smycles-meta leading-relaxed text-ink"
          role={result.kind === "error" ? "alert" : "status"}
        >
          {result.kind === "error" ? result.message : <OutcomeMessage outcome={result.outcome} />}
        </p>
      ) : null}
    </Modal>
  );
}

// The row's actions say which command genesis accepts next: after a refused
// mail the person is already bound, so a second press is a resend, not a
// second invitation.
function nextMode(outcome: PersonLoginInvitationOutcome, row: PersonLoginAssignmentFieldsFragment): InviteDialogMode | null {
  if (!RETRYABLE.has(outcome)) return null;
  if (row.actions.includes(PersonLoginAction.Invite)) return "invite";
  if (row.actions.includes(PersonLoginAction.Resend)) return "resend";
  return null;
}

function OutcomeMessage({ outcome }: { outcome: PersonLoginInvitationOutcome }) {
  switch (outcome) {
    case PersonLoginInvitationOutcome.Invited:
      return <FormattedMessage
        id="loginAssignment.outcome.invited"
        defaultMessage="Done. The login is bound to this person and the invitation is on its way."
      />;
    case PersonLoginInvitationOutcome.Resent:
      return <FormattedMessage
        id="loginAssignment.outcome.resent"
        defaultMessage="The invitation was sent again to the same address."
      />;
    case PersonLoginInvitationOutcome.RecipientUpdated:
      return <FormattedMessage
        id="loginAssignment.outcome.recipientUpdated"
        defaultMessage="The address was corrected and the invitation sent to the new one."
      />;
    case PersonLoginInvitationOutcome.AlreadyAssigned:
      return <FormattedMessage
        id="loginAssignment.outcome.alreadyAssigned"
        defaultMessage="This person already has a login. Nothing was changed or sent."
      />;
    case PersonLoginInvitationOutcome.EmailConflict:
      return <FormattedMessage
        id="loginAssignment.outcome.emailConflict"
        defaultMessage="Another account already uses this address. Nothing was changed; pick a different address."
      />;
    case PersonLoginInvitationOutcome.AccountChanged:
      return <FormattedMessage
        id="loginAssignment.outcome.accountChanged"
        defaultMessage="The account changed in the meantime. Nothing was sent; check the status and try again."
      />;
    case PersonLoginInvitationOutcome.SendFailed:
      return <FormattedMessage
        id="loginAssignment.outcome.sendFailed"
        defaultMessage="The mail was refused and not delivered. The login stays bound, so sending again is safe."
      />;
    case PersonLoginInvitationOutcome.SendUncertain:
      return <FormattedMessage
        id="loginAssignment.outcome.sendUncertain"
        defaultMessage="The login is bound, but whether the mail went out is unclear. It may have arrived, so do not send it again."
      />;
    case PersonLoginInvitationOutcome.CreateUncertain:
      return <FormattedMessage
        id="loginAssignment.outcome.createUncertain"
        defaultMessage="The outcome is unclear: an account may or may not have been created, and nothing was sent. Check this person in Keycloak before trying again."
      />;
  }
}
